// src/Component/NavBar.jsx
import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FaBell, FaSearch, FaPlus } from "react-icons/fa";
import DefaultAvatar from "../assets/user.png";
import { useUser } from "../context/UserAttrContext";
import { t } from "../i18n";
import "./NavBar.css";
import { useNotificationsContext } from "../context/NotificationsContext";
import { clearSession } from "../utils/auth";

export default function NavBar({ logout }) {
  const { user } = useUser();
  const [isMobile, setIsMobile] = useState(window.innerWidth < 700);
  const [menuOpen, setMenuOpen] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  // toggle full-width mobile search bar
  const [showMobileSearch, setShowMobileSearch] = useState(false);

  const navigate = useNavigate();
  const {
    badgeNotifications,
    dismissBadge,
    pendingRequests,
    updateConnectionStatus,
  } = useNotificationsContext();

  const hasNotif = badgeNotifications.length > 0 || pendingRequests.length > 0;

  function handleLogout() {
    clearSession();
    if (logout) {
      logout();
    } else {
      navigate("/");
    }
  }

  function closeNotifications() {
    setShowNotifications(false);
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    const q = searchQuery.trim();
    if (q) navigate(`/filtersearch?query=${encodeURIComponent(q)}`);
    // restore normal navbar
    setShowMobileSearch(false);
  }

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 700);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return (
    <header className="navbar-wrapper">

      {/* MOBILE: full-width search bar */}
      {isMobile && showMobileSearch ? (
        <div className="navbar-top">
          <form className="searchWrapper mobile" onSubmit={handleSearchSubmit}>
            <FaSearch className="searchIcon" />
            <input
              autoFocus
              type="text"
              className="animatedSearchInput"
              placeholder={t("search_website")}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <button type="submit" className="goSearchBtn">{t("go")}</button>
          </form>
        </div>
      ) : (

        <div className="navbar-top">
          <Link to="/" className="logo">PunchLine</Link>

          {!isMobile && (
            <form className="searchWrapper" onSubmit={handleSearchSubmit}>
              <FaSearch className="searchIcon" />
              <input
                type="text"
                className="animatedSearchInput"
                placeholder={t("search_website")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <button type="submit" className="goSearchBtn">{t("go")}</button>
            </form>
          )}

          <div className="top-right">
            {isMobile && !showMobileSearch && (
              <button
                className="icon-btn search"
                onClick={() => setShowMobileSearch(true)}
              >
                <FaSearch />
              </button>
            )}

            {!isMobile && (
              <Link to="/CreatePost" className="btn-create">{t("create+")}</Link>
            )}
            {isMobile && !showMobileSearch && (
              <Link to="/CreatePost" className="btn-create">{<FaPlus />}</Link>
            )}

            <button
              className={`icon-btn bell${hasNotif ? " active" : ""}`}
              onClick={() => setShowNotifications(v => !v)}
            >
              <FaBell />
              {hasNotif && <span className="notification-badge"></span>}
            </button>

            <div
              className="profile-btn"
              onClick={() => setShowProfileDropdown(v => !v)}
            >
              <img
                src={user?.picture || DefaultAvatar}
                alt="Profile"
                className="profile-icon"
              />
              {showProfileDropdown && (
                <nav className="profile-dropdown">
                  <Link to="/Profile">{t("profile")}</Link>
                  <Link to="/Settings">{t("settings")}</Link>
                  <button className="logout" onClick={handleLogout}>
                    {t("logout")}
                  </button>
                </nav>
              )}
            </div>

            {isMobile && (
              <button
                className="icon-btn menu"
                onClick={() => setMenuOpen(v => !v)}
              >
                ☰
              </button>
            )}
          </div>
        </div>
      )}
      {isMobile && menuOpen && (
          <nav className="mobile-nav">
            <Link to="/games">{t("games")}</Link>
            <Link to="/friends">{t("friends")}</Link>
            <Link to="/styleguide">{t("style_guide")}</Link>
            <div className="about-wrapper">
              <div
                className="about-us"
                onClick={() => setAboutOpen((v) => !v)}
              >
                {t("about_us")} ▾
              </div>
              {aboutOpen && (
                <div className="about-dropdown-mobile">
                  <Link to="/AboutMeDaShawn">DaShawn</Link>
                  <Link to="/Mdjim">Jim</Link>
                  <Link to="/aboutme-tauhidur">Tauhidur</Link>
                  <Link to="/Pandahxb">Kai</Link>
                  <Link to="/Arafat">Arafat</Link>
                </div>
              )}
            </div>

            <Link to="/Profile">{t("profile")}</Link>
          </nav>
        )}

      {/* secondary navigation */}
      <nav className="navbar-secondary">
        <Link to="/GameSelect">{t("games")}</Link>
        <Link to="/friends">{t("friends")}</Link>
        <Link to="/styleguide">{t("style_guide")}</Link>
        <div className="about-us" onClick={() => setAboutOpen(v => !v)}>
           {t("about_us")} ▾
          {aboutOpen && (
            <div className="about-dropdown">
              <Link to="/AboutMeDaShawn">DaShawn</Link>
              <Link to="/Mdjim">Jim</Link>
              <Link to="/aboutme-tauhidur">Tauhidur</Link>
              <Link to="/Pandahxb">Kai</Link>
              <Link to="/Arafat">Arafat</Link>
            </div>
          )}
        </div>
      </nav>

      {/* notifications dropdown */}
      {showNotifications && (
        <div className="notif-dropdown">
          <header className="notification-header">
            <h5>{t("notifications")}</h5>
            <button onClick={closeNotifications}>×</button>
          </header>
          <section>
            <h6>{t("friend_requests")}</h6>
            {pendingRequests.length > 0 ? (
              pendingRequests.map((r) => {
                const raw = r.fromUser.attributes.username;
                const displayName = raw.length > 8 ? raw.slice(0, 8) + "…" : raw;
                return (
                  <div key={r.id} className="request-item">
                    <Link to={`/Profile/${r.fromUser.id}`} className="request-username">
                      {displayName}
                    </Link>
                    <div className="actions">
                      <button className="accept-button" onClick={() => updateConnectionStatus(r.id, "active")}>
                        {t("accept")}
                      </button>
                      <button className="reject-button" onClick={() => updateConnectionStatus(r.id, "rejected")}>
                        {t("reject")}
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p>{t("no_new_requests")}</p>
            )}
          </section>
          <section>
            <h6>{t("badges")}</h6>
            {badgeNotifications.length > 0 ? (
              badgeNotifications.map((b, i) => (
                <div key={b.id} className="notif-item">
                  <div className="notif-item-header">
                    <div className="name">{b.name}</div>
                    <div className="time">{new Date(b.timestamp).toLocaleTimeString()}</div>
                    <button className="close-btn" onClick={() => dismissBadge(i)}>×</button>
                  </div>
                  <div className="description">{b.message}</div>
                </div>
              ))
            ) : (
              <p>{t("no_new_badge_alerts")}</p>
            )}
          </section>
        </div>
      )}
    </header>
  );
}
